import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";
import {
  resourcePersonAuthInitialState,
  createResourcePersonAuthActions,
} from "./resourcePersonAuthStore";
import {
  resourcePersonCourseInitialState,
  createResourcePersonCourseActions,
} from "./resourcePersonCourseStore";
import {
  resourcePersonDashboardInitialState,
  createResourcePersonDashboardActions,
} from "./resourcePersonDashboardStore";
import {
  resourcePersonEarningsInitialState,
  createResourcePersonEarningsActions,
} from "./resourcePersonEarningsStore";
import {
  resourcePersonWithdrawalInitialState,
  createResourcePersonWithdrawalActions,
} from "./resourcePersonWithdrawalStore";
import {
  resourcePersonBookInitialState,
  createResourcePersonBookActions,
} from "./resourcePersonBookStore";
import {
  resourcePersonLessonInitialState,
  createResourcePersonLessonActions,
} from "./resourcePersonLessonStore";
import {
  resourcePersonLearningPathInitialState,
  createResourcePersonLearningPathActions,
} from "./resourcePersonLearningPathStore";
import {
  resourcePersonSubmissionInitialState,
  createResourcePersonSubmissionActions,
} from "./resourcePersonSubmissionStore";
import {
  resourcePersonJournalInitialState,
  createResourcePersonJournalActions,
} from "./resourcePersonJournalStore";
import {
  resourcePersonBankInitialState,
  createResourcePersonBankActions,
} from "./resourcePersonBankStore";
import {
  resourcePersonUploadInitialState,
  createResourcePersonUploadActions,
} from "./resourcePersonUploadStore";
import {
  resourcePersonMarketplaceInitialState,
  createResourcePersonMarketplaceActions,
} from "./resourcePersonMarketplaceStore";

/**
 * The single global resource person store. Actions from every slice are
 * spread onto it, along with the required initial state.
 */
export const useResourcePersonStore = create((set, get) => ({
  // Slice state
  ...resourcePersonAuthInitialState,
  ...resourcePersonCourseInitialState,
  ...resourcePersonDashboardInitialState,
  ...resourcePersonEarningsInitialState,
  ...resourcePersonWithdrawalInitialState,
  ...resourcePersonBookInitialState,
  ...resourcePersonLessonInitialState,
  ...resourcePersonLearningPathInitialState,
  ...resourcePersonSubmissionInitialState,
  ...resourcePersonJournalInitialState,
  ...resourcePersonBankInitialState,
  ...resourcePersonUploadInitialState,
  ...resourcePersonMarketplaceInitialState,

  // Slice actions
  ...createResourcePersonAuthActions(set, get),
  ...createResourcePersonCourseActions(set, get),
  ...createResourcePersonDashboardActions(set, get),
  ...createResourcePersonEarningsActions(set, get),
  ...createResourcePersonWithdrawalActions(set, get),
  ...createResourcePersonBookActions(set, get),
  ...createResourcePersonLessonActions(set, get),
  ...createResourcePersonLearningPathActions(set, get),
  ...createResourcePersonSubmissionActions(set, get),
  ...createResourcePersonJournalActions(set, get),
  ...createResourcePersonBankActions(set, get),
  ...createResourcePersonUploadActions(set, get),
  ...createResourcePersonMarketplaceActions(set),
}));

// ── Dedicated selector hooks (Zustand best practice) ─────────────────────────

/** Auth + login selectors. */
export const useResourcePersonAuth = () =>
  useResourcePersonStore(
    useShallow((s) => ({
      isAuthenticated: s.isAuthenticated,
      user: s.user,
      loginLoading: s.loginLoading,
      loginError: s.loginError,
      logoutLoading: s.logoutLoading,
      authActionLoading: s.authActionLoading,
      authActionError: s.authActionError,
      login: s.login,
      logout: s.logout,
      refresh: s.refresh,
      fetchMe: s.fetchMe,
      updateMe: s.updateMe,
      forgotPassword: s.forgotPassword,
      resetPassword: s.resetPassword,
      changePassword: s.changePassword,
      clearAuth: s.clearAuth,
    }))
  );

/** Course list, detail, analytics, student, and mutation selectors. */
export const useResourcePersonCourses = () =>
  useResourcePersonStore(
    useShallow((s) => ({
      courses: s.courses,
      coursesLoading: s.coursesLoading,
      coursesError: s.coursesError,
      coursesTotal: s.coursesTotal,
      coursesPages: s.coursesPages,
      courseDetail: s.courseDetail,
      courseDetailLoading: s.courseDetailLoading,
      courseDetailError: s.courseDetailError,
      courseStudents: s.courseStudents,
      courseStudentsLoading: s.courseStudentsLoading,
      courseStudentsError: s.courseStudentsError,
      courseStudentsTotal: s.courseStudentsTotal,
      courseStudentsPages: s.courseStudentsPages,
      courseAnalytics: s.courseAnalytics,
      courseAnalyticsLoading: s.courseAnalyticsLoading,
      courseAnalyticsError: s.courseAnalyticsError,
      courseMutationLoading: s.courseMutationLoading,
      courseMutationError: s.courseMutationError,
      fetchCourses: s.fetchCourses,
      createCourse: s.createCourse,
      fetchCourseById: s.fetchCourseById,
      updateCourse: s.updateCourse,
      submitCourse: s.submitCourse,
      unsubmitCourse: s.unsubmitCourse,
      deleteCourse: s.deleteCourse,
      fetchCourseStudents: s.fetchCourseStudents,
      fetchCourseAnalytics: s.fetchCourseAnalytics,
    }))
  );

/** Categories dropdown, lesson + question authoring selectors. */
export const useResourcePersonLessons = () =>
  useResourcePersonStore(
    useShallow((s) => ({
      lessons: s.lessons,
      lessonsLoading: s.lessonsLoading,
      lessonsError: s.lessonsError,
      lessonMutationLoading: s.lessonMutationLoading,
      lessonMutationError: s.lessonMutationError,
      questions: s.questions,
      questionsLoading: s.questionsLoading,
      questionsError: s.questionsError,
      questionMutationLoading: s.questionMutationLoading,
      questionMutationError: s.questionMutationError,
      assessments: s.assessments,
      assessmentsLoading: s.assessmentsLoading,
      assessmentsError: s.assessmentsError,
      assessmentMutationLoading: s.assessmentMutationLoading,
      assessmentMutationError: s.assessmentMutationError,
      lessonResources: s.lessonResources,
      lessonResourcesLoading: s.lessonResourcesLoading,
      lessonResourcesError: s.lessonResourcesError,
      lessonVideos: s.lessonVideos,
      lessonVideosLoading: s.lessonVideosLoading,
      lessonVideosError: s.lessonVideosError,
      lessonMediaMutationLoading: s.lessonMediaMutationLoading,
      lessonMediaMutationError: s.lessonMediaMutationError,
      rpCategories: s.rpCategories,
      rpCategoriesLoading: s.rpCategoriesLoading,
      rpCategoriesError: s.rpCategoriesError,
      rpCategoryMutationLoading: s.rpCategoryMutationLoading,
      rpCategoryMutationError: s.rpCategoryMutationError,
      modules: s.modules,
      modulesLoading: s.modulesLoading,
      modulesError: s.modulesError,
      moduleMutationLoading: s.moduleMutationLoading,
      moduleMutationError: s.moduleMutationError,
      fetchRpCategories: s.fetchRpCategories,
      createRpCategory: s.createRpCategory,
      fetchModules: s.fetchModules,
      // Read-only count that never writes the shared `modules` slice, so the
      // library grid can count many courses without clobbering the builder's list.
      fetchModuleCount: s.fetchModuleCount,
      createModule: s.createModule,
      updateModule: s.updateModule,
      deleteModule: s.deleteModule,
      reorderModules: s.reorderModules,
      fetchLessons: s.fetchLessons,
      fetchLessonCount: s.fetchLessonCount,
      createLesson: s.createLesson,
      updateLesson: s.updateLesson,
      deleteLesson: s.deleteLesson,
      reorderLessons: s.reorderLessons,
      fetchQuestions: s.fetchQuestions,
      fetchAssessments: s.fetchAssessments,
      createAssessment: s.createAssessment,
      updateAssessment: s.updateAssessment,
      deleteAssessment: s.deleteAssessment,
      reorderAssessments: s.reorderAssessments,
      createQuestion: s.createQuestion,
      updateQuestion: s.updateQuestion,
      deleteQuestion: s.deleteQuestion,
      fetchLessonResources: s.fetchLessonResources,
      createLessonResource: s.createLessonResource,
      updateLessonResource: s.updateLessonResource,
      deleteLessonResource: s.deleteLessonResource,
      reorderLessonResources: s.reorderLessonResources,
      fetchLessonVideos: s.fetchLessonVideos,
      createLessonVideo: s.createLessonVideo,
      updateLessonVideo: s.updateLessonVideo,
      deleteLessonVideo: s.deleteLessonVideo,
      reorderLessonVideos: s.reorderLessonVideos,
      clearLessonAuthoring: s.clearLessonAuthoring,
    }))
  );

/** Learning-path (bundle) authoring selectors. */
export const useResourcePersonLearningPaths = () =>
  useResourcePersonStore(
    useShallow((s) => ({
      learningPaths: s.learningPaths,
      learningPathsLoading: s.learningPathsLoading,
      learningPathsError: s.learningPathsError,
      learningPathsTotal: s.learningPathsTotal,
      learningPathsPages: s.learningPathsPages,
      learningPathDetail: s.learningPathDetail,
      learningPathDetailLoading: s.learningPathDetailLoading,
      learningPathDetailError: s.learningPathDetailError,
      learningPathAnalytics: s.learningPathAnalytics,
      learningPathAnalyticsLoading: s.learningPathAnalyticsLoading,
      learningPathAnalyticsError: s.learningPathAnalyticsError,
      learningPathMutationLoading: s.learningPathMutationLoading,
      learningPathMutationError: s.learningPathMutationError,
      fetchLearningPaths: s.fetchLearningPaths,
      fetchLearningPathById: s.fetchLearningPathById,
      createLearningPath: s.createLearningPath,
      updateLearningPath: s.updateLearningPath,
      deleteLearningPath: s.deleteLearningPath,
      addCourseToPath: s.addCourseToPath,
      removeCourseFromPath: s.removeCourseFromPath,
      reorderPathCourses: s.reorderPathCourses,
      submitLearningPath: s.submitLearningPath,
      unsubmitLearningPath: s.unsubmitLearningPath,
      fetchLearningPathAnalytics: s.fetchLearningPathAnalytics,
    }))
  );

/** Submission grading selectors (Stage 7). */
export const useResourcePersonSubmissions = () =>
  useResourcePersonStore(
    useShallow((s) => ({
      submissions: s.submissions,
      submissionsLoading: s.submissionsLoading,
      submissionsError: s.submissionsError,
      submissionsTotal: s.submissionsTotal,
      submissionsPages: s.submissionsPages,
      gradeMutationLoading: s.gradeMutationLoading,
      gradeMutationError: s.gradeMutationError,
      fetchSubmissions: s.fetchSubmissions,
      fetchPendingSubmissionCount: s.fetchPendingSubmissionCount,
      gradeSubmission: s.gradeSubmission,
    }))
  );

/** Cloudinary media-upload selectors. */
export const useResourcePersonUploads = () =>
  useResourcePersonStore(
    useShallow((s) => ({
      imageUploadLoading: s.imageUploadLoading,
      imageUploadError: s.imageUploadError,
      lastImageUpload: s.lastImageUpload,
      documentUploadLoading: s.documentUploadLoading,
      documentUploadError: s.documentUploadError,
      lastDocumentUpload: s.lastDocumentUpload,
      uploadImage: s.uploadImage,
      uploadDocument: s.uploadDocument,
      deleteUpload: s.deleteUpload,
    }))
  );

/** Marketplace authoring (project topics, seminars, research materials, manuals). */
export const useResourcePersonMarketplace = () =>
  useResourcePersonStore(
    useShallow((s) => ({
      marketplaceItems: s.marketplaceItems,
      marketplaceLoading: s.marketplaceLoading,
      marketplaceError: s.marketplaceError,
      marketplaceTotal: s.marketplaceTotal,
      marketplacePages: s.marketplacePages,
      marketplaceResourceType: s.marketplaceResourceType,
      marketplaceItem: s.marketplaceItem,
      marketplaceItemLoading: s.marketplaceItemLoading,
      marketplaceItemError: s.marketplaceItemError,
      marketplaceMutationLoading: s.marketplaceMutationLoading,
      marketplaceMutationError: s.marketplaceMutationError,
      fetchMarketplaceItems: s.fetchMarketplaceItems,
      fetchMarketplaceItem: s.fetchMarketplaceItem,
      createMarketplaceItem: s.createMarketplaceItem,
      updateMarketplaceItem: s.updateMarketplaceItem,
      deleteMarketplaceItem: s.deleteMarketplaceItem,
      submitMarketplaceItem: s.submitMarketplaceItem,
      unsubmitMarketplaceItem: s.unsubmitMarketplaceItem,
      clearMarketplaceItem: s.clearMarketplaceItem,
    }))
  );

export const useResourcePersonDashboard = () =>
  useResourcePersonStore(
    useShallow((s) => ({
      dashboard: s.dashboard,
      dashboardLoading: s.dashboardLoading,
      dashboardError: s.dashboardError,
      fetchDashboard: s.fetchDashboard,
    }))
  );

export const useResourcePersonWithdrawals = () =>
  useResourcePersonStore(
    useShallow((s) => ({
      withdrawals: s.withdrawals,
      withdrawalsTotal: s.withdrawalsTotal,
      withdrawalsPages: s.withdrawalsPages,
      withdrawalDetail: s.withdrawalDetail,
      withdrawalsLoading: s.withdrawalsLoading,
      withdrawalsError: s.withdrawalsError,
      withdrawalMutationLoading: s.withdrawalMutationLoading,
      withdrawalMutationError: s.withdrawalMutationError,
      fetchWithdrawals: s.fetchWithdrawals,
      fetchWithdrawalById: s.fetchWithdrawalById,
      createWithdrawal: s.createWithdrawal,
      cancelWithdrawal: s.cancelWithdrawal,
      clearWithdrawalDetail: s.clearWithdrawalDetail,
    }))
  );

export const useResourcePersonBooks = () =>
  useResourcePersonStore(
    useShallow((s) => ({
      books: s.books,
      booksLoading: s.booksLoading,
      booksError: s.booksError,
      booksTotal: s.booksTotal,
      booksPages: s.booksPages,
      bookDetail: s.bookDetail,
      bookDetailLoading: s.bookDetailLoading,
      bookDetailError: s.bookDetailError,
      bookDownloads: s.bookDownloads,
      bookDownloadsLoading: s.bookDownloadsLoading,
      bookDownloadsError: s.bookDownloadsError,
      bookAnalytics: s.bookAnalytics,
      bookAnalyticsLoading: s.bookAnalyticsLoading,
      bookAnalyticsError: s.bookAnalyticsError,
      bookMutationLoading: s.bookMutationLoading,
      bookMutationError: s.bookMutationError,
      fetchBooks: s.fetchBooks,
      createBook: s.createBook,
      fetchBookById: s.fetchBookById,
      updateBook: s.updateBook,
      submitBook: s.submitBook,
      unsubmitBook: s.unsubmitBook,
      deleteBook: s.deleteBook,
      fetchBookDownloads: s.fetchBookDownloads,
      fetchBookAnalytics: s.fetchBookAnalytics,
    }))
  );

export const useResourcePersonEarnings = () =>
  useResourcePersonStore(
    useShallow((s) => ({
      earningsSummary: s.earningsSummary,
      earningsAnalytics: s.earningsAnalytics,
      earningsPending: s.earningsPending,
      earningsAvailable: s.earningsAvailable,
      earningsReserved: s.earningsReserved,
      earningsLedger: s.earningsLedger,
      earningsLedgerTotal: s.earningsLedgerTotal,
      earningsLedgerPages: s.earningsLedgerPages,
      earningsHistory: s.earningsHistory,
      earningsHistoryTotal: s.earningsHistoryTotal,
      earningsHistoryPages: s.earningsHistoryPages,
      earningsLoading: s.earningsLoading,
      earningsError: s.earningsError,
      fetchEarningsSummary: s.fetchEarningsSummary,
      fetchEarningsAnalytics: s.fetchEarningsAnalytics,
      fetchEarningsPending: s.fetchEarningsPending,
      fetchEarningsAvailable: s.fetchEarningsAvailable,
      fetchEarningsReserved: s.fetchEarningsReserved,
      fetchEarningsLedger: s.fetchEarningsLedger,
      fetchEarningsHistory: s.fetchEarningsHistory,
      fetchAllEarnings: s.fetchAllEarnings,
    }))
  );

export const useResourcePersonJournals = () =>
  useResourcePersonStore(
    useShallow((s) => ({
      journals: s.journals,
      journalsLoading: s.journalsLoading,
      journalsError: s.journalsError,
      journalsTotal: s.journalsTotal,
      journalsPages: s.journalsPages,
      journalDetail: s.journalDetail,
      journalDetailLoading: s.journalDetailLoading,
      journalDetailError: s.journalDetailError,
      journalDownloads: s.journalDownloads,
      journalDownloadsLoading: s.journalDownloadsLoading,
      journalDownloadsError: s.journalDownloadsError,
      journalAnalytics: s.journalAnalytics,
      journalAnalyticsLoading: s.journalAnalyticsLoading,
      journalAnalyticsError: s.journalAnalyticsError,
      journalMutationLoading: s.journalMutationLoading,
      journalMutationError: s.journalMutationError,
      fetchJournals: s.fetchJournals,
      createJournal: s.createJournal,
      fetchJournalById: s.fetchJournalById,
      updateJournal: s.updateJournal,
      submitJournal: s.submitJournal,
      unsubmitJournal: s.unsubmitJournal,
      deleteJournal: s.deleteJournal,
      fetchJournalDownloads: s.fetchJournalDownloads,
      fetchJournalAnalytics: s.fetchJournalAnalytics,
    }))
  );

export const useResourcePersonBankDetails = () =>
  useResourcePersonStore(
    useShallow((s) => ({
      bankDetails: s.bankDetails,
      bankDetailsLoading: s.bankDetailsLoading,
      bankDetailsError: s.bankDetailsError,
      bankMutationLoading: s.bankMutationLoading,
      bankMutationError: s.bankMutationError,
      banks: s.banks,
      banksLoading: s.banksLoading,
      banksError: s.banksError,
      fetchBankDetails: s.fetchBankDetails,
      createBankDetails: s.createBankDetails,
      saveBankDetails: s.saveBankDetails,
      updateBankDetails: s.updateBankDetails,
      fetchBanks: s.fetchBanks,
    }))
  );

export default useResourcePersonStore;